import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic CRUD page. `fields` is an array of:
 *   { name, label, type: 'text'|'number'|'textarea'|'date'|'select', options?, colSpan? }
 * `columns` lists which fields to render in the table (subset of field names).
 */
export default function GenericCrudPage({
  overline, title, description, endpoint, fields, columns, defaults, testid,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaults);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => api.get(endpoint).then((r) => setItems(r.data)), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form };
      // Coerce numbers
      fields.forEach((f) => {
        if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0;
      });
      if (editId) await api.put(`${endpoint}/${editId}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editId ? "Updated" : "Created");
      setOpen(false); setEditId(null); setForm(defaults);
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const edit = (item) => {
    setForm({ ...defaults, ...item });
    setEditId(item.id);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!confirm("Delete this record?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed", { description: e?.response?.data?.detail || e.message }); }
  };

  const renderField = (f) => {
    const val = form[f.name] ?? "";
    const set = (v) => setForm({ ...form, [f.name]: v });
    if (f.type === "select") {
      return (
        <Select value={String(val || "")} onValueChange={set}>
          <SelectTrigger data-testid={`${testid}-form-${f.name}`} className="rounded-sm mt-1"><SelectValue placeholder={f.label} /></SelectTrigger>
          <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "textarea") {
      return <Textarea data-testid={`${testid}-form-${f.name}`} value={val} onChange={(e) => set(e.target.value)} rows={3} className="rounded-sm mt-1" />;
    }
    return <Input data-testid={`${testid}-form-${f.name}`} type={f.type} value={val} onChange={(e) => set(e.target.value)} className="rounded-sm mt-1" />;
  };

  const displayValue = (item, col) => {
    const v = item[col];
    if (v === undefined || v === null || v === "") return "—";
    const fld = fields.find((f) => f.name === col);
    if (fld?.type === "number" && ["amount", "salary", "contract_value"].includes(col)) {
      return `₹${Number(v).toLocaleString("en-IN")}`;
    }
    if (fld?.type === "select") {
      return <span className="font-mono text-[10px] px-2 py-0.5 border border-zinc-300 rounded-sm">{v}</span>;
    }
    return String(v);
  };

  return (
    <div data-testid={`${testid}-page`}>
      <PageHeader
        overline={overline}
        title={title}
        description={description}
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(defaults); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid={`${testid}-new-btn`} className="bg-orange-600 hover:bg-orange-700 rounded-sm">
                <Plus className="h-4 w-4 mr-2" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editId ? "Edit" : "Create"} {title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {fields.map((f) => (
                  <div key={f.name} className={f.colSpan === 2 ? "col-span-2" : ""}>
                    <Label className="overline">{f.label}</Label>
                    {renderField(f)}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button data-testid={`${testid}-save-btn`} onClick={save} className="bg-orange-600 hover:bg-orange-700 rounded-sm">
                  {editId ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              {columns.map((c) => {
                const f = fields.find((ff) => ff.name === c);
                return <th key={c} className="px-4 py-2.5 overline">{f?.label || c}</th>;
              })}
              <th className="px-4 py-2.5 overline text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="text-center py-16 text-zinc-400 font-mono text-xs">No records yet.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`${testid}-row-${it.id}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                {columns.map((c) => <td key={c} className="px-4 py-3">{displayValue(it, c)}</td>)}
                <td className="px-4 py-3 text-right">
                  <button data-testid={`${testid}-edit-${it.id}`} onClick={() => edit(it)} className="p-1.5 hover:bg-zinc-100 rounded-sm inline-flex mr-1"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`${testid}-delete-${it.id}`} onClick={() => remove(it.id)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm inline-flex"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
